import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

import { toast, registerToast } from '../MapboxMap/toastService';
import { circleTwoClickMode, markerCircleMode, DrawExtraControls, makeCircleFeature, drawStyles } from '../MapboxMap/drawModes';
import DrawnShapesList from '../MapboxMap/DrawnShapesList';
import ShapeAttributesEditor from '../MapboxMap/ShapeAttributesEditor';

import EmpreendimentoServiceCrawler from '../../services/empreendimentosServicesCrawler';
import geolocationService from '../../services/geolocationService';
import KmlService from '../../services/KmlService';
import ModalPolosGeradores from '../ModalPolosGeradores';
import Legenda from '../MapaRevendaLocacao/Legenda';
import tipos from '../MapaRevendaLocacao/tipos';
import { estados } from '../../util/estados';
import colors from '../../util/colors';
import getBrowserGeolocation from '../../util/geolocation';

import FormFiltersRevenda from './FormFiltersRevenda';
import ResultsPanel from './ResultsPanel';
import ImovelModal from './ImovelModal';
import '../mapa.css';
import './MapaRevendaLocacaoMapbox.css';

const SOURCE_ID = 'imoveis';
const BAIRROS_SOURCE_ID = 'bairros';
const CLUSTER_LAYERS = ['clusters', 'cluster-count', 'unclustered-point'];
const EMPTY_FC = { type: 'FeatureCollection', features: [] };
const DEFAULT_ZOOM = 13;
// Alinhado ao gate de marcadores: ao selecionar cidade/endereço/estado, o zoom já
// fica no nível em que os marcadores do trecho visível são carregados.
const SELECTED_ZOOM = 13;
// Debounce do recarregamento de clusters ao mover/dar zoom no mapa.
const MOVE_DEBOUNCE_MS = 400;
// Grid mínimo do servidor (graus) acima do CLUSTER_MAX_ZOOM: só imóveis dentro
// dessa distância (~3 m, mesmo endereço/prédio) permanecem agrupados ao aproximar.
// Um cluster com espalhamento maior que isso ainda se separa dando zoom.
const EXACT_GRID_DEG = 0.00003;

const MAP_STYLE_STORAGE_KEY = 'geobrain.mrl-mapbox.style';
const MAP_STYLES = {
    light: 'mapbox://styles/mapbox/streets-v12',
    dark: 'mapbox://styles/mapbox/dark-v11',
};
const readStoredMapStyle = () => {
    try {
        const v = localStorage.getItem(MAP_STYLE_STORAGE_KEY);
        return v === 'dark' || v === 'light' ? v : 'light';
    } catch {
        return 'light';
    }
};

// Cidades com limites de bairros (GeoJSON) disponíveis, no formato "UF-Cidade".
const BAIRROS_CITIES = new Set(window.bairrosCities || []);

// Ícones dos marcadores por tipo de imóvel × tipo de negócio (reuso do mapeamento
// do mapa Google, MapaRevendaLocacao/tipos.js). Cada PNG é carregado sob um nome
// derivado do próprio arquivo (ex.: /img/markers/apartamentos.png → "apartamentos").
const ICON_GROUPS = ['apartamentosTypes', 'casasTypes', 'ruralTypes', 'terrenosTypes', 'industrialTypes', 'comercialTypes'];
const iconNameFromUrl = (url) => url.split('/').pop().replace('.png', '');
const MARKER_ICONS = (() => {
    const map = new Map();
    ICON_GROUPS.forEach((g) => ['venda', 'aluguel'].forEach((neg) => {
        const url = tipos[g].icons[neg];
        map.set(iconNameFromUrl(url), url);
    }));
    return [...map.entries()].map(([name, url]) => ({ name, url }));
})();
// Índice tipo → { venda, aluguel } de nomes de ícone, montado uma vez. Evita
// varrer os 6 grupos (com array.includes) por feature — decisivo para cidades
// grandes (ex.: Curitiba, ~30 mil imóveis).
const TIPO_TO_ICONS = (() => {
    const map = new Map();
    ICON_GROUPS.forEach((g) => {
        const venda = iconNameFromUrl(tipos[g].icons.venda);
        const aluguel = iconNameFromUrl(tipos[g].icons.aluguel);
        tipos[g].types.forEach((t) => { if (!map.has(t)) map.set(t, { venda, aluguel }); });
    });
    return map;
})();
const getMarkerIconName = (tipo, isVenda) => {
    const entry = tipo ? TIPO_TO_ICONS.get(tipo) : null;
    return entry ? (isVenda ? entry.venda : entry.aluguel) : null;
};

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_SCRIPT_ID = 'google-maps-places-script';
function loadGoogleMapsPlaces() {
    if (window.google?.maps?.places) return Promise.resolve();
    if (document.getElementById(GOOGLE_SCRIPT_ID)) {
        return new Promise((resolve) => {
            document.getElementById(GOOGLE_SCRIPT_ID).addEventListener('load', resolve, { once: true });
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = GOOGLE_SCRIPT_ID;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&language=pt-BR&region=BR`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

const EMPTY_FILTERS = {
    projeto: null,
    tipo_negocio: 'Venda',
    tipo_empreendimento: [],
    estadosSelecionados: null,
    estado: [],
    cidade: [],
    bairros: [],
    bairrosOptions: [],
    area_maior_que: null,
    area_menor_que: null,
    valor_maior_que: null,
    valor_menor_que: null,
    valor_metro_maior_que: null,
    valor_metro_menor_que: null,
    quartos: [],
    vagas: [],
};

class MapaRevendaLocacaoMapbox extends Component {
    state = {
        left_side_bar_visible: true,
        right_panel_visible: false,
        active_tab: 'filtros',
        mapStyle: readStoredMapStyle(),
        loading: false,
        loadingClusters: false,
        loadingKml: false,
        showAlertCity: false,
        totalEmpreendimentos: 0,
        showEmpreendimentos: true,
        showBairros: false,
        bairrosAvailable: false,
        drawnShapes: [],
        searchAreas: [],
        // Filtros (componente controlado)
        filters: { ...EMPTY_FILTERS },
        projeto_name: [{ value: null, label: 'Selecione' }],
        // Detalhe do imóvel
        showImovelModal: false,
        imovelDetail: null,
        loadingImovel: false,
        // Editor de atributos de forma
        shapeEditorOpen: false,
        shapeEditing: null,
        // Salvar projeto
        modalSalvarProjetoOpen: false,
        projectName: '',
        savingProject: false,
        // Polos geradores
        show_modal_polos_geradores: false,
        polos_geradores_current_showing: [],
        polos_geradores_current_grouped: [],
        polos_geradores_current_cache_key: '',
    };

    // Objetos do mapa como instance fields (fora do state para não re-renderizar).
    containerRef = React.createRef();
    searchInputRef = React.createRef();
    kmlInputRef = React.createRef();
    toastRef = React.createRef();
    map = null;
    draw = null;
    drawExtras = null;
    addressMarker = null;
    allFeatures = [];
    currentGeojson = null;
    bairrosCache = {};
    authUserCities = [];
    _hidingIds = new Set();
    // Contexto da busca ativa, usado para recarregar os marcadores por viewport.
    // { mode: 'cidade'|'estado'|'shapes', state, cidadeArea } — null quando não há busca.
    searchContext = null;
    markerAbort = null;      // AbortController da busca de clusters em voo (cancela stale)
    moveTimer = null;        // debounce do moveend
    lastClusterZoom = null;  // último zoom (inteiro) com que os clusters foram carregados
    lastTileKey = null;      // última chave de tile carregada (evita refetch no mesmo tile)

    componentDidMount() {
        registerToast(this.toastRef);

        const apiKey = window.mapboxApiKey;
        if (!apiKey || !this.containerRef.current) {
            if (!apiKey) console.error('[MapaRevendaLocacaoMapbox] window.mapboxApiKey ausente');
            return;
        }

        mapboxgl.accessToken = apiKey;
        this.map = new mapboxgl.Map({
            container: this.containerRef.current,
            style: MAP_STYLES[this.state.mapStyle],
            center: [-49.1929618, -25.551136],
            zoom: DEFAULT_ZOOM,
            preserveDrawingBuffer: true,
        });

        this.map.on('error', (e) => console.error('[MapaRevendaLocacaoMapbox] erro:', e.error));
        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
        this.map.addControl(
            new mapboxgl.GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: true }),
            'top-right'
        );

        this.setupDrawTools();
        this.map.on('load', () => this.initializeLayers());
        // Recarrega os marcadores do trecho visível ao mover/dar zoom (só em busca por cidade).
        this.map.on('moveend', this.handleMoveEnd);

        this.centerOnBrowserLocation();
        this.setupGooglePlaces();
        this.fetchUserCities();
        this.getProjects();
    }

    componentWillUnmount() {
        clearTimeout(this.moveTimer);
        this.markerAbort?.abort();
        if (this.map) this.map.remove();
    }

    centerOnBrowserLocation = async () => {
        try {
            const pos = await getBrowserGeolocation();
            if (pos?.lat && pos?.lng) this.map?.flyTo({ center: [pos.lng, pos.lat], zoom: DEFAULT_ZOOM });
        } catch { /* mantém o centro padrão */ }
    };

    fetchUserCities = async () => {
        try {
            const res = await fetch(route('web.auth_user_cities'));
            this.authUserCities = await res.json();
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao buscar cidades autorizadas:', e);
        }
    };

    // ===== Camadas =====
    initializeLayers = async () => {
        await this.loadMarkerImages();
        this.setupBairrosLayers();
        this.setupClusterLayers();

        if (this.currentGeojson) {
            this.map.getSource(SOURCE_ID)?.setData(this.currentGeojson);
        }
        if (this.state.showBairros) {
            this.renderBairros(this.state.filters.cidade);
        }
        if (!this.state.showEmpreendimentos) {
            this.setEmpreendimentosVisibility(false);
        }
    };

    loadMarkerImages = () =>
        Promise.all(
            MARKER_ICONS.map(({ name, url }) =>
                new Promise((resolve) => {
                    if (this.map.hasImage(name)) return resolve();
                    this.map.loadImage(url, (err, image) => {
                        if (err || !image) return resolve();
                        if (!this.map.hasImage(name)) this.map.addImage(name, image);
                        resolve();
                    });
                })
            )
        );

    setupBairrosLayers = () => {
        if (this.map.getSource(BAIRROS_SOURCE_ID)) return;
        this.map.addSource(BAIRROS_SOURCE_ID, { type: 'geojson', data: EMPTY_FC });
        this.map.addLayer({
            id: 'bairros-fill', type: 'fill', source: BAIRROS_SOURCE_ID,
            paint: { 'fill-color': '#0d6efd', 'fill-opacity': 0.08 },
        });
        this.map.addLayer({
            id: 'bairros-outline', type: 'line', source: BAIRROS_SOURCE_ID,
            paint: { 'line-color': '#0d6efd', 'line-width': 1.5 },
        });
        this.map.addLayer({
            id: 'bairros-label', type: 'symbol', source: BAIRROS_SOURCE_ID,
            layout: { 'text-field': ['get', 'name'], 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'], 'text-size': 11 },
            paint: { 'text-color': '#0a4dad', 'text-halo-color': '#ffffff', 'text-halo-width': 1.2 },
        });
    };

    // Fonte NÃO clusterizada no cliente: o servidor (PostGIS) já entrega clusters
    // (total>1) e pontos individuais (total==1) para o zoom/área atuais. As camadas
    // se distinguem pela propriedade `total`.
    setupClusterLayers = () => {
        if (this.map.getSource(SOURCE_ID)) return;

        this.map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

        this.map.addLayer({
            id: 'clusters', type: 'circle', source: SOURCE_ID, filter: ['>', ['get', 'total'], 1],
            paint: {
                'circle-color': ['step', ['get', 'total'], '#51bbd6', 50, '#f1f075', 200, '#f28cb1'],
                'circle-radius': ['step', ['get', 'total'], 16, 50, 22, 200, 28, 1000, 34],
                'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff',
            },
        });
        this.map.addLayer({
            id: 'cluster-count', type: 'symbol', source: SOURCE_ID, filter: ['>', ['get', 'total'], 1],
            layout: {
                'text-field': ['to-string', ['get', 'total']],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
        });
        this.map.addLayer({
            id: 'unclustered-point', type: 'symbol', source: SOURCE_ID, filter: ['==', ['get', 'total'], 1],
            layout: { 'icon-image': ['get', 'icon'], 'icon-size': 0.6, 'icon-allow-overlap': true, 'icon-anchor': 'bottom' },
        });

        // Clique num cluster: se os imóveis se separam ao aproximar, dá zoom (o
        // servidor re-clusteriza). Se são co-localizados (mesmo prédio → spread ~0),
        // aproximar não adianta: abre a lista dos imóveis do ponto.
        this.map.on('click', 'clusters', (e) => {
            const f = e.features?.[0];
            if (!f) return;
            const z = this.map.getZoom();
            const spread = f.properties.spread; // extensão em graus (undefined em cache antigo)
            // Não separa em nenhum zoom quando os pontos estão praticamente no mesmo
            // ponto (mesmo endereço/prédio) — cabem no grid mínimo do servidor.
            const naoSepara = spread != null && Number(spread) <= EXACT_GRID_DEG;
            if (z >= 18 || naoSepara) {
                this.openClusterList(f.geometry.coordinates, spread);
            } else {
                this.map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(z + 2, 18) });
            }
        });

        const hoverPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 });
        this.map.on('mouseenter', 'unclustered-point', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
            const p = e.features[0].properties;
            const address = [p.log, p.bai, p.cidade].filter((x) => x && String(x).length).join(', ');
            hoverPopup
                .setLngLat(e.features[0].geometry.coordinates.slice())
                .setHTML(`<strong>${p.anu || 'Anunciante não informado'}</strong><br/><small>${address || 'Endereço não disponível'}</small>`)
                .addTo(this.map);
        });
        this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
            hoverPopup.remove();
        });
        this.map.on('click', 'unclustered-point', (e) => {
            const f = e.features?.[0];
            if (!f) return;
            hoverPopup.remove();
            this.openImovelModal(f.properties?.id);
        });
        this.map.on('mouseenter', 'clusters', () => { this.map.getCanvas().style.cursor = 'pointer'; });
        this.map.on('mouseleave', 'clusters', () => { this.map.getCanvas().style.cursor = ''; });
    };

    setEmpreendimentosVisibility = (visible) => {
        const v = visible ? 'visible' : 'none';
        CLUSTER_LAYERS.forEach((id) => this.map?.getLayer(id) && this.map.setLayoutProperty(id, 'visibility', v));
    };

    toggleEmpreendimentos = (enabled) => {
        this.setState({ showEmpreendimentos: enabled });
        this.setEmpreendimentosVisibility(enabled);
    };

    // ===== Estilo do mapa (claro/escuro) =====
    setMapStyle = (style) => {
        if (style !== 'light' && style !== 'dark') return;
        this.setState({ mapStyle: style });
        try { localStorage.setItem(MAP_STYLE_STORAGE_KEY, style); } catch { /* localStorage indisponível */ }

        const drawn = this.draw?.getAll();
        this.map.setStyle(MAP_STYLES[style]);
        this.map.once('style.load', () => {
            this.initializeLayers();
            if (drawn?.features?.length) this.draw.set(drawn);
        });
    };

    // ===== Ferramentas de desenho =====
    setupDrawTools = () => {
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
            userProperties: true,
            styles: drawStyles,
            modes: {
                ...MapboxDraw.modes,
                draw_rectangle: DrawRectangle,
                draw_circle_two_click: circleTwoClickMode,
                draw_marker_circle: markerCircleMode,
            },
        });
        this.map.addControl(this.draw, 'top-left');
        this.drawExtras = new DrawExtraControls({ draw: this.draw });
        this.map.addControl(this.drawExtras, 'top-left');

        this.map.on('draw.create', this.handleDrawCreate);
        this.map.on('draw.update', this.handleDrawUpdate);
        this.map.on('draw.delete', this.handleDrawDelete);
    };

    handleDrawCreate = (e) => {
        const feature = e.features?.[0];
        if (!feature || (feature.geometry?.type !== 'Polygon' && feature.geometry?.type !== 'MultiPolygon')) return;

        const shapeType = feature.properties?.shapeType;
        const typeLabels = { circle: 'Círculo', marker_circle: 'Círculo', rectangle: 'Retângulo' };
        const type = typeLabels[shapeType] || 'Polígono';
        const idx = this.state.drawnShapes.length;
        const color = colors[idx % colors.length];
        const areaId = `drawn-${Date.now()}`;

        const styleAttrs = { fillColor: color, fillOpacity: 0.3, strokeColor: '#000000', strokeWeight: 3 };
        Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(feature.id, k, v));

        const shape = {
            drawId: feature.id,
            areaId,
            type,
            name: `${type} ${idx + 1}`,
            color,
            visible: true,
            feature: this.draw.get(feature.id) || feature,
            center: feature.properties?.center || null,
            radiusMeters: feature.properties?.radiusMeters || null,
            ...styleAttrs,
        };

        this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, shape] }), () => this.syncShapes());
    };

    handleDrawUpdate = (e) => {
        (e.features || []).forEach((feat) => {
            this.setState((prev) => ({
                drawnShapes: prev.drawnShapes.map((s) => s.drawId === feat.id
                    ? { ...s, feature: feat, center: feat.properties?.center || s.center, radiusMeters: feat.properties?.radiusMeters || s.radiusMeters }
                    : s),
            }));
        });
        this.syncShapes();
    };

    handleDrawDelete = (e) => {
        const deletedIds = (e.features || [])
            .map((f) => f.id)
            .filter((id) => {
                if (this._hidingIds.has(id)) { this._hidingIds.delete(id); return false; }
                return true;
            });
        if (deletedIds.length === 0) return;

        this.setState((prev) => {
            const removedAreaIds = prev.drawnShapes.filter((s) => deletedIds.includes(s.drawId)).map((s) => s.areaId);
            return {
                drawnShapes: prev.drawnShapes.filter((s) => !deletedIds.includes(s.drawId)),
                searchAreas: prev.searchAreas.filter((a) => !removedAreaIds.includes(a.id)),
            };
        }, () => this.syncShapes());
    };

    // ===== Lista de formas (aba Ferramentas) =====
    updateShape = (drawId, patch) => this.setState((prev) => ({
        drawnShapes: prev.drawnShapes.map((s) => s.drawId === drawId ? { ...s, ...patch } : s),
    }));

    centerOnShape = (shape) => {
        const feat = shape.visible ? this.draw?.get(shape.drawId) : shape.feature;
        if (!feat?.geometry?.coordinates) return;
        const bounds = new mapboxgl.LngLatBounds();
        const walk = (coords) => Array.isArray(coords[0]) ? coords.forEach(walk) : bounds.extend(coords);
        walk(feat.geometry.coordinates);
        this.map.fitBounds(bounds, { padding: 60, duration: 600 });
    };

    toggleShapeVisibility = (shape) => {
        if (shape.visible) {
            const feat = this.draw?.get(shape.drawId) || shape.feature;
            this._hidingIds.add(shape.drawId);
            this.draw?.delete(shape.drawId);
            this.updateShape(shape.drawId, { visible: false, feature: feat });
        } else {
            const [id] = this.draw?.add(shape.feature) || [];
            this.updateShape(shape.drawId, { visible: true, drawId: id ?? shape.drawId });
        }
    };

    setShapeRadius = (shape, radiusMeters) => {
        if (!shape.center || !(radiusMeters > 0)) return;
        const shapeType = shape.feature?.properties?.shapeType || 'circle';
        const updated = makeCircleFeature(shape.center, radiusMeters, shapeType);
        updated.properties = {
            ...updated.properties,
            fillColor: shape.fillColor, fillOpacity: shape.fillOpacity,
            strokeColor: shape.strokeColor, strokeWeight: shape.strokeWeight,
        };
        updated.id = shape.drawId;
        if (shape.visible) this.draw?.add(updated);
        this.updateShape(shape.drawId, { radiusMeters, feature: updated });
        this.syncShapes();
    };

    editShapeAttributes = (shape) => this.setState({ shapeEditing: shape, shapeEditorOpen: true });
    closeShapeEditor = () => this.setState({ shapeEditorOpen: false, shapeEditing: null });

    applyShapeAttributes = (shape, attrs) => {
        if (!shape) return;
        const base = (shape.visible && this.draw?.get(shape.drawId)) || shape.feature;
        const updated = {
            ...base, id: shape.drawId,
            properties: {
                ...base.properties,
                fillColor: attrs.fillColor, fillOpacity: attrs.fillOpacity,
                strokeColor: attrs.strokeColor, strokeWeight: attrs.strokeWeight,
            },
        };
        if (shape.type === 'Círculo' && attrs.radiusMeters > 0 && shape.center) {
            const st = updated.properties?.shapeType || 'circle';
            const geo = makeCircleFeature(shape.center, attrs.radiusMeters, st);
            updated.geometry = geo.geometry;
            updated.properties = { ...updated.properties, radiusMeters: attrs.radiusMeters };
        }
        if (shape.visible) {
            this.draw?.add(updated);
            this.draw?.changeMode('simple_select', { featureIds: [] });
        }
        const name = (attrs.name || '').trim() || shape.name;
        const cor = attrs.fillColor === 'transparent' ? attrs.strokeColor : attrs.fillColor;
        this.updateShape(shape.drawId, {
            name, color: cor, fillColor: attrs.fillColor, fillOpacity: attrs.fillOpacity,
            strokeColor: attrs.strokeColor, strokeWeight: attrs.strokeWeight,
            radiusMeters: shape.type === 'Círculo' && attrs.radiusMeters > 0 ? attrs.radiusMeters : shape.radiusMeters,
            feature: updated,
        });
        this.setState((prev) => ({
            searchAreas: prev.searchAreas.map((a) => a.id === shape.areaId ? { ...a, name, color: cor } : a),
        }));
        this.closeShapeEditor();
    };

    removeShape = (shape) => {
        if (shape.visible) this.draw?.delete(shape.drawId);
        this._hidingIds.delete(shape.drawId);
        this.setState((prev) => ({
            drawnShapes: prev.drawnShapes.filter((s) => s.drawId !== shape.drawId),
            searchAreas: prev.searchAreas.filter((a) => a.id !== shape.areaId),
        }), () => this.syncShapes());
    };

    // Remove um card do painel (botão X). Se for um card de forma, remove também a
    // forma do mapa; se for de cidade/estado, só tira o card (histórico do painel).
    removeArea = (id) => {
        const area = this.state.searchAreas.find((a) => a.id === id);
        if (area?.kind === 'shape') {
            const shape = this.state.drawnShapes.find((s) => s.areaId === id);
            if (shape) { this.removeShape(shape); return; }
        }
        this.setState((prev) => {
            const areas = prev.searchAreas.filter((a) => a.id !== id);
            return { searchAreas: areas, right_panel_visible: areas.length > 0 };
        }, () => this.restoreMapContext());
    };

    // Redetermina o que o mapa deve exibir após uma remoção (card ou forma): formas
    // visíveis restantes (se houver), senão o card de cidade/estado mais recente que
    // ainda estiver no painel, senão limpa o mapa. Sem isso, remover a área que
    // estava ativa no mapa deixava os clusters da área removida presos na tela em
    // vez de recarregar a área restante.
    restoreMapContext = () => {
        const shapes = this.state.drawnShapes.filter((s) => s.visible);
        if (shapes.length > 0) {
            this.searchShapes(shapes, this.crawlerState());
            return;
        }
        const locationArea = this.state.searchAreas.find((a) => a.kind === 'location');
        if (locationArea) {
            this.searchContext = {
                mode: locationArea.typeLabel === 'Estado' ? 'estado' : 'cidade',
                state: locationArea.crawlerState,
                shapes: [],
            };
            this.lastClusterZoom = null;
            this.lastTileKey = null;
            this.loadClusters();
            return;
        }
        this.searchContext = null;
        this.markerAbort?.abort();
        this.lastTileKey = null;
        this.lastClusterZoom = null;
        this.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
    };

    // ===== Filtros =====
    handleFilterChange = (field, value) => {
        this.setState((prev) => ({ filters: { ...prev.filters, [field]: value } }));
    };

    handleToggleQuarto = (q) => {
        this.setState((prev) => {
            const quartos = prev.filters.quartos.includes(q)
                ? prev.filters.quartos.filter((x) => x !== q)
                : [...prev.filters.quartos, q];
            return { filters: { ...prev.filters, quartos } };
        });
    };

    // Vaga 4 representa "4 ou mais" → expande para 4..16 no payload (paridade Google).
    handleToggleVaga = (v) => {
        this.setState((prev) => {
            const has = prev.filters.vagas.includes(v);
            let vagas;
            if (has) {
                vagas = prev.filters.vagas.filter((x) => x !== v);
                if (v === 4) vagas = vagas.filter((x) => x < 4);
            } else {
                vagas = [...prev.filters.vagas, v];
                if (v === 4) vagas = [...new Set([...vagas, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16])];
            }
            return { filters: { ...prev.filters, vagas } };
        });
    };

    handleEstadoChange = async (uf) => {
        this.setState((prev) => ({
            filters: { ...prev.filters, estadosSelecionados: uf, estado: uf ? [uf] : [], cidade: [], bairros: [], bairrosOptions: [] },
        }));
        await this.centralizarMapaNaCapital(uf);
    };

    handleCidadeChange = async (option) => {
        if (!option) {
            this.setState((prev) => ({ filters: { ...prev.filters, cidade: [], bairros: [], bairrosOptions: [] } }));
            return;
        }
        const uf = option.value.split('-')[0].trim();
        const bairrosOptions = await this.fetchCrawlerBairros(option.label);
        this.setState((prev) => ({
            filters: { ...prev.filters, cidade: [option], estado: [uf], estadosSelecionados: uf, bairros: [], bairrosOptions: bairrosOptions || [] },
        }));
        this.refreshBairrosAvailability([option]);

        try {
            const address = `${option.label} ${uf} - Brasil`;
            const location = await geolocationService.location(address);
            if (location?.latitude && location?.longitude) {
                this.map.flyTo({ center: [location.longitude, location.latitude], zoom: SELECTED_ZOOM });
            }
        } catch { /* posicionamento é best-effort */ }
    };

    centralizarMapaNaCapital = async (uf) => {
        if (!this.map || !uf) return;
        try {
            const res = await axios.get(route('state.capital', { state: uf }));
            const { lat, lng } = res.data || {};
            if (lat == null || lng == null) return;
            this.map.flyTo({ center: [Number(lng), Number(lat)], zoom: SELECTED_ZOOM });
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao centralizar na capital:', e);
        }
    };

    fetchCrawlerBairros = async (cidade) => {
        try {
            const res = await axios.get('/bairros-crawler/json/' + cidade);
            return res.data || [];
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao buscar bairros:', e);
            return [];
        }
    };

    // ===== Motor de busca =====
    // Estado no formato que o serviço do MR Crawler espera (buildPayload).
    crawlerState = () => {
        const f = this.state.filters;
        return {
            tipo_empreendimento: f.tipo_empreendimento,
            padrao: [],
            area_maior_que: f.area_maior_que ?? '',
            area_menor_que: f.area_menor_que ?? '',
            quartos: f.quartos,
            vagas: f.vagas,
            tipo_negocio: f.tipo_negocio,
            valor_menor_que: f.valor_menor_que ?? '',
            valor_maior_que: f.valor_maior_que ?? '',
            valor_metro_maior_que: f.valor_metro_maior_que ?? '',
            valor_metro_menor_que: f.valor_metro_menor_que ?? '',
            cidade: f.cidade,
            estado: f.estado,
            bairros: f.bairros,
            get_meta_data: false,
        };
    };

    reloadEmpreendimentos = async () => {
        const shapes = this.state.drawnShapes.filter((s) => s.visible);
        const hasShapes = shapes.length > 0;
        const f = this.state.filters;
        const hasCity = f.cidade.length > 0 || f.estado.length > 0;

        if (!hasShapes && !hasCity) {
            toast.warn('Desenhe uma área no mapa ou selecione uma cidade.');
            this.setState({ showAlertCity: true });
            return;
        }

        this.setState({ loading: true, showAlertCity: false });
        const state = this.crawlerState();

        try {
            // O painel ACUMULA: uma nova busca entra no topo e as anteriores permanecem
            // (paridade com o mapa antigo).
            if (hasShapes) {
                this.searchShapes(shapes, state, true); // BUSCAR: recarrega todas as formas (filtros podem ter mudado)
            } else {
                // ===== Busca por cidade/estado ===== → novo card no topo (id único).
                const buscaPorEstado = f.cidade.length === 0 && f.estado.length > 0;
                const area = {
                    id: `loc-${Date.now()}`,
                    kind: 'location',
                    name: buscaPorEstado ? f.estado.join(', ') : (f.cidade[0]?.label || 'Cidade'),
                    type: 'cidade',
                    typeLabel: buscaPorEstado ? 'Estado' : 'Cidade',
                    color: colors[this.state.searchAreas.length % colors.length],
                    tipo_negocio: f.tipo_negocio,
                    count_buildings: 0,     // preenchido pela agregação (soma dos totais)
                    query_id: null,         // obtido sob demanda ao exportar (Excel)
                    crawlerState: state,    // usado na exportação lazy
                    loading: true,
                };
                this.searchContext = { mode: buscaPorEstado ? 'estado' : 'cidade', state, shapes: [] };
                this.setState((prev) => ({ searchAreas: [area, ...prev.searchAreas], right_panel_visible: true }));
                this.loadMetadataForAreas([area], [], false, state);
                this.lastClusterZoom = null;
                this.lastTileKey = null;
                this.loadClusters();
            }

            if (this.state.showBairros) this.renderBairros(f.cidade);
            this.refreshBairrosAvailability(f.cidade);
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro na busca:', e);
            toast.error('Erro ao carregar imóveis.');
        } finally {
            this.setState({ loading: false });
        }
    };

    // Sincroniza os cards das formas desenhadas (recentes no topo), preservando os
    // cards de cidade/estado já no painel; re-renderiza os clusters das formas.
    // `force`: recarrega os metadados de TODAS as formas (ex.: BUSCAR após mudar
    // filtros). Sem force (eventos de desenho), preserva os cards já carregados cuja
    // geometria não mudou — só carrega o novo/movido, evitando flicker.
    searchShapes = (shapes, state, force = false) => {
        const f = this.state.filters;
        const prevById = new Map(this.state.searchAreas.map((a) => [a.id, a]));

        const shapeAreas = shapes.map((shape) => {
            const geom = (this.draw.get(shape.drawId) || shape.feature).geometry;
            const prev = prevById.get(shape.areaId);
            const inalterada = prev && prev.kind === 'shape' && !prev.loading
                && JSON.stringify(prev.shapeGeometry) === JSON.stringify(geom);
            if (!force && inalterada) {
                return { ...prev, name: shape.name, color: shape.color, radius: shape.radiusMeters ?? null };
            }
            return {
                id: shape.areaId,
                kind: 'shape',
                name: shape.name,
                type: shape.type,
                typeLabel: shape.type,
                color: shape.color,
                tipo_negocio: f.tipo_negocio,
                count_buildings: 0,
                query_id: null,
                shapeGeometry: geom,
                loading: true,
                radius: shape.radiusMeters ?? null,
            };
        });

        this.searchContext = { mode: 'shapes', state, shapes };
        this.fitBoundsToShapes(shapes);
        this.setState((prev) => {
            const locations = prev.searchAreas.filter((a) => a.kind !== 'shape');
            return { searchAreas: [...[...shapeAreas].reverse(), ...locations], right_panel_visible: true };
        });

        const toLoad = shapeAreas.filter((a) => a.loading);
        if (toLoad.length > 0) this.loadMetadataForAreas(toLoad, shapes, true, state);
        this.lastClusterZoom = null;
        this.lastTileKey = null;
        this.loadClusters();
    };

    // Reação a eventos de desenho (criar/mover/remover formas). NÃO dispara busca de
    // cidade: se não há mais formas, limpa só os cards de forma + clusters, mantendo
    // os cards de cidade/estado acumulados no painel.
    syncShapes = () => {
        const shapes = this.state.drawnShapes.filter((s) => s.visible);
        if (shapes.length === 0) {
            this.setState((prev) => {
                const locations = prev.searchAreas.filter((a) => a.kind !== 'shape');
                return { searchAreas: locations, right_panel_visible: locations.length > 0 };
            }, () => this.restoreMapContext());
            return;
        }
        this.searchShapes(shapes, this.crawlerState());
    };

    // ===== Clusterização no servidor =====
    // Recarrega os clusters ao mover/dar zoom. Cidade/estado: o viewport mudou →
    // sempre recarrega. Formas: a área é fixa, só o zoom muda o grid → recarrega
    // apenas quando o zoom inteiro muda.
    handleMoveEnd = () => {
        const ctx = this.searchContext;
        if (!ctx) return;
        if (ctx.mode === 'shapes' && Math.round(this.map.getZoom()) === this.lastClusterZoom) return;
        clearTimeout(this.moveTimer);
        this.moveTimer = setTimeout(() => this.loadClusters(), MOVE_DEBOUNCE_MS);
    };

    // bbox do viewport alinhado a uma grade de tiles (em graus, dependente do zoom),
    // expandido para fora. Isso estabiliza o retângulo consultado: pans próximos e
    // usuários diferentes vendo a mesma região geram a MESMA requisição → cache do
    // servidor + possibilidade de pular refetch. Retorna o polígono e uma chave.
    tileBbox = (zoom) => {
        const b = this.map.getBounds();
        const tile = 360 / Math.pow(2, Math.max(1, zoom - 2)); // ~tamanho do tile em graus
        const floor = (v) => Math.floor(v / tile) * tile;
        const ceil = (v) => Math.ceil(v / tile) * tile;
        const w = floor(b.getWest()), s = floor(b.getSouth()), e = ceil(b.getEast()), n = ceil(b.getNorth());
        return {
            poly: { type: 'Polygon', coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]] },
            key: `${zoom}:${w.toFixed(4)}:${s.toFixed(4)}:${e.toFixed(4)}:${n.toFixed(4)}`,
        };
    };

    loadClusters = async () => {
        const ctx = this.searchContext;
        if (!ctx || !this.map?.getSource(SOURCE_ID)) return;

        const zoom = Math.round(this.map.getZoom());
        this.lastClusterZoom = zoom;

        // Áreas geográficas da consulta: cidade/estado → tile do viewport; formas →
        // a geometria de cada forma (uma consulta por forma).
        let geoms;
        if (ctx.mode === 'shapes') {
            geoms = ctx.shapes.map((s) => (this.draw.get(s.drawId) || s.feature).geometry);
        } else {
            const { poly, key } = this.tileBbox(zoom);
            // Mesmo tile/zoom da última carga → os dados já estão na tela; não refaz.
            if (key === this.lastTileKey) return;
            this.lastTileKey = key;
            geoms = [poly];
        }

        this.markerAbort?.abort();
        const controller = new AbortController();
        this.markerAbort = controller;
        this.setState({ loadingClusters: true });

        try {
            const responses = await Promise.all(
                geoms.map((geo) => EmpreendimentoServiceCrawler.getClusters(geo, zoom, ctx.state, { signal: controller.signal }))
            );
            if (controller.signal.aborted) return;

            const features = this.buildClusterFeatures(responses.flatMap((r) => r.data?.features || []));
            this.currentGeojson = { type: 'FeatureCollection', features };
            this.map.getSource(SOURCE_ID)?.setData(this.currentGeojson);
        } catch (e) {
            if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || controller.signal.aborted) return;
            console.error('[MapaRevendaLocacaoMapbox] erro ao carregar clusters:', e);
        } finally {
            // Só apaga o loader se esta ainda for a última requisição (pan/zoom rápido
            // inicia outra antes desta terminar — a nova mantém o loader ligado).
            if (this.markerAbort === controller) this.setState({ loadingClusters: false });
        }
    };

    // Popup reutilizado com a lista de imóveis de um cluster co-localizado.
    clusterListPopup = new mapboxgl.Popup({ maxWidth: '340px', closeButton: true });

    // Abre a lista dos imóveis num ponto (cluster que não se separa por zoom).
    openClusterList = async (center, spread) => {
        const ctx = this.searchContext;
        if (!ctx) return;

        // bbox pequeno em volta do centro, cobrindo o espalhamento do cluster (+ margem).
        const half = (Number(spread) || 0) / 2 + 0.0004; // ~44 m de margem
        const [lng, lat] = center;
        const geo = {
            type: 'Polygon',
            coordinates: [[
                [lng - half, lat - half], [lng + half, lat - half],
                [lng + half, lat + half], [lng - half, lat + half], [lng - half, lat - half],
            ]],
        };

        this.setState({ loadingClusters: true });
        try {
            const res = await EmpreendimentoServiceCrawler.getPointsInArea(geo, ctx.state);
            const pts = res.data || [];
            if (pts.length === 0) return;

            const items = pts.map((p) => {
                const addr = [p.log, p.bai].filter(Boolean).join(', ');
                return `<button type="button" class="mrl-cluster-item" data-id="${p.id}">`
                    + `<span class="mrl-cluster-item-tipo">${p.tipo || 'Imóvel'}</span>`
                    + `<span class="mrl-cluster-item-sub">${[p.anu, addr].filter(Boolean).join(' — ') || 'Sem descrição'}</span>`
                    + `</button>`;
            }).join('');

            const html = `<div class="mrl-cluster-list">`
                + `<div class="mrl-cluster-list-header">${pts.length} imóvei${pts.length > 1 ? 's' : ''} neste ponto</div>`
                + `<div class="mrl-cluster-list-body">${items}</div></div>`;

            this.clusterListPopup.setLngLat(center).setHTML(html).addTo(this.map);
            this.clusterListPopup.getElement()
                .querySelectorAll('.mrl-cluster-item')
                .forEach((btn) => btn.addEventListener('click', () => {
                    this.clusterListPopup.remove();
                    this.openImovelModal(btn.dataset.id);
                }));
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao listar imóveis do cluster:', e);
            toast.error('Não foi possível listar os imóveis deste ponto.');
        } finally {
            this.setState({ loadingClusters: false });
        }
    };

    // Prepara as features vindas do servidor para renderização:
    // - clusters (total>1): passam direto (círculo + contagem).
    // - pontos (total==1): filtra por cidade autorizada e atribui o ícone por
    //   tipo × negócio (mutação in-place; sem ícone reconhecido → omitido).
    buildClusterFeatures = (features) => {
        const isVenda = this.state.filters.tipo_negocio.toLowerCase() === 'venda';
        const auth = this.authUserCities || [];
        const authSet = auth.length > 0 ? new Set(auth) : null;
        const out = [];
        for (const feat of features) {
            const p = feat.properties;
            if (!p) continue;
            if (p.total > 1) { out.push(feat); continue; }
            if (authSet && p.cidade && !authSet.has(p.cidade)) continue;
            const icon = getMarkerIconName(p.tipo, isVenda);
            if (!icon) continue;
            p.icon = icon;
            out.push(feat);
        }
        return out;
    };

    fitBoundsToShapes = (shapes) => {
        if (!this.map || !shapes.length) return;
        const bounds = new mapboxgl.LngLatBounds();
        const walk = (coords) => Array.isArray(coords[0]) ? coords.forEach(walk) : bounds.extend(coords);
        shapes.forEach((s) => {
            const feat = this.draw.get(s.drawId) || s.feature;
            if (feat?.geometry?.coordinates) walk(feat.geometry.coordinates);
        });
        if (!bounds.isEmpty()) this.map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 16 });
    };

    updateArea = (id, patch) => this.setState((prev) => ({
        searchAreas: prev.searchAreas.map((a) => a.id === id ? { ...a, ...patch } : a),
    }));

    loadMetadataForAreas = async (areas, shapes, hasShapes, state) => {
        await Promise.all(areas.map(async (area) => {
            try {
                // Cidade/estado → agregação por cidade/estado; forma → por polígono.
                const geometry = area.shapeGeometry || null;
                const fetchAgr = (agr) => geometry
                    ? EmpreendimentoServiceCrawler.getMetadataWithinPolygon(geometry, state, agr)
                    : EmpreendimentoServiceCrawler.getMetadataWithinCidade(state, agr);

                const [m, mq, mp] = await Promise.all([
                    fetchAgr(['tipo']),
                    fetchAgr(['tipo', 'quartos']),
                    fetchAgr(['tipo', 'padrao']),
                ]);
                const metadata = m.data?.data ?? [];
                // O total da área é a soma dos totais da agregação (não baixamos as
                // features individuais — o mapa mostra clusters do servidor).
                this.updateArea(area.id, {
                    metadata,
                    metadataQuartos: mq.data?.data ?? [],
                    metadataPadrao: mp.data?.data ?? [],
                    count_buildings: metadata.reduce((sum, row) => sum + Number(row.total || 0), 0),
                    loading: false,
                });
            } catch (e) {
                console.error('[MapaRevendaLocacaoMapbox] erro ao carregar metadados da área:', e);
                this.updateArea(area.id, { loading: false });
            }
        }));
    };

    // ===== Detalhe do imóvel =====
    openImovelModal = async (id) => {
        if (!id || this.state.loadingImovel) return;
        this.setState({ showImovelModal: true, loadingImovel: true, imovelDetail: null });
        try {
            const data = await EmpreendimentoServiceCrawler.getEmpreendimento(id);
            this.setState({ imovelDetail: data });
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao carregar detalhe:', e);
            toast.error('Não foi possível carregar os detalhes do imóvel.');
            this.setState({ showImovelModal: false });
        } finally {
            this.setState({ loadingImovel: false });
        }
    };

    closeImovelModal = () => this.setState({ showImovelModal: false, imovelDetail: null });

    // ===== Excel + Polos =====
    // O Excel precisa do query_id de uma consulta de LISTA (o crawler cacheia o
    // query builder sob esse id por 3h). Como o mapa só carrega clusters, o query_id
    // é obtido sob demanda no clique: cidade/estado via lista da cidade; forma via
    // lista dentro do polígono.
    handleExportExcel = async (area) => {
        try {
            let queryId = area.query_id;
            if (!queryId) {
                toast.info('Preparando a exportação…');
                const res = area.shapeGeometry
                    ? await EmpreendimentoServiceCrawler.getEmpreendimentosWithinPolygon(area.shapeGeometry, this.crawlerState())
                    : await EmpreendimentoServiceCrawler.getEmpreendimentoOnCidade(area.crawlerState);
                queryId = res.headers['x-query-id'];
            }
            if (!queryId) {
                toast.error('Não foi possível preparar a exportação desta área.');
                return;
            }
            const res = await EmpreendimentoServiceCrawler.exportExcel(queryId);
            toast.success(res.data?.message || 'Exportação solicitada.');
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao exportar Excel:', e);
            toast.error('Erro ao solicitar a exportação.');
        }
    };

    handleShowPolos = (area) => {
        const polos = area.polos_geradores || [];
        const agrupamentos = [];
        polos.forEach((polo) => {
            const category = polo.main_category || polo.categories?.[0];
            const found = agrupamentos.find((a) => a.category === category);
            if (found) found.count++;
            else agrupamentos.push({ category, count: 1 });
        });
        this.setState({
            polos_geradores_current_showing: polos,
            polos_geradores_current_grouped: agrupamentos,
            polos_geradores_current_cache_key: area.polos_geradores_cache_key || '',
            show_modal_polos_geradores: true,
        });
    };

    // ===== Limites de bairros =====
    handleToggleBairros = (enabled) => {
        this.setState({ showBairros: enabled });
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;
        if (!enabled) {
            this.map.getSource(BAIRROS_SOURCE_ID).setData(EMPTY_FC);
            return;
        }
        const cidades = this.state.filters.cidade;
        if (cidades.length === 0) toast.info('Selecione e busque uma cidade para ver os limites de bairros.');
        this.renderBairros(cidades);
    };

    renderBairros = async (cidades) => {
        if (!this.map?.getSource(BAIRROS_SOURCE_ID)) return;
        const groups = await Promise.all((cidades || []).map((c) => this.fetchBairrosGeojson(c.value)));
        this.map.getSource(BAIRROS_SOURCE_ID)?.setData({ type: 'FeatureCollection', features: groups.flat() });
    };

    fetchBairrosGeojson = async (value) => {
        if (this.bairrosCache[value]) return this.bairrosCache[value];
        const idx = value.indexOf('-');
        if (idx === -1) return [];
        const uf = value.slice(0, idx);
        const cidade = value.slice(idx + 1);
        try {
            const res = await axios.get(`/bairros/geojson/${encodeURIComponent(cidade)}/${encodeURIComponent(uf)}`);
            const features = res.data?.features || [];
            this.bairrosCache[value] = features;
            return features;
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao carregar limites de bairros:', e);
            return [];
        }
    };

    refreshBairrosAvailability = (cidades) => {
        const available = (cidades || []).some((c) => BAIRROS_CITIES.has(c.value));
        this.setState({ bairrosAvailable: available });
        if (!available && this.state.showBairros) {
            this.setState({ showBairros: false });
            this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        }
    };

    // ===== KML: exportação / importação =====
    handleDownloadKml = async () => {
        const features = this.draw?.getAll().features || [];
        const polygons = [];
        const circles = [];
        features.forEach((f) => {
            if (f.geometry?.type !== 'Polygon') return;
            const isCircle = ['circle', 'marker_circle'].includes(f.properties?.shapeType);
            if (isCircle && f.properties?.center && f.properties?.radiusMeters) {
                circles.push({ latitude: f.properties.center[1], longitude: f.properties.center[0], radius: f.properties.radiusMeters });
            } else {
                const ring = f.geometry.coordinates?.[0] || [];
                polygons.push(ring.map(([lng, lat]) => [lat, lng]));
            }
        });
        const empreendimentos = (this.currentGeojson?.features || []).map((f) => {
            const p = f.properties || {};
            return {
                _id: p.id, nome: p.anu || '', tipo_empreendimento: p.tipo || '',
                endereco: p.log || '', bairro: p.bai || '', marker: p.icon || '',
                location: { coordinates: f.geometry?.coordinates || null },
            };
        });
        if (polygons.length === 0 && circles.length === 0 && empreendimentos.length === 0) {
            toast.info('Nada para exportar. Desenhe uma forma ou faça uma busca primeiro.');
            return;
        }
        try {
            this.setState({ loadingKml: true });
            await KmlService.download({ polygons, circles, empreendimentos });
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao exportar KML:', e);
            toast.error('Erro ao exportar o KML.');
        } finally {
            this.setState({ loadingKml: false });
        }
    };

    handleImportKmlFile = async (file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            this.setState({ loadingKml: true });
            const response = await axios.post(route('transform-kml'), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (response.status !== 200 || !Array.isArray(response.data) || response.data.length === 0) {
                toast.warn('Nenhum polígono encontrado no arquivo.');
                return;
            }
            await this.addImportedPolygons(response.data);
            toast.success(`${response.data.length} forma(s) importada(s).`);
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao importar KML/KMZ:', e);
            toast.error('Erro ao importar o arquivo KML/KMZ.');
        } finally {
            this.setState({ loadingKml: false });
        }
    };

    addImportedPolygons = async (paths) => {
        if (!this.draw) return;
        const newShapes = [];
        const allBounds = new mapboxgl.LngLatBounds();
        let firstCenter = null;

        paths.forEach((path, i) => {
            if (!Array.isArray(path) || path.length < 3) return;
            const ring = path.map(([lat, lng]) => [lng, lat]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);

            const [drawId] = this.draw.add({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } });
            const idx = this.state.drawnShapes.length + newShapes.length;
            const color = colors[idx % colors.length];
            const areaId = `drawn-${Date.now()}-${i}`;
            const styleAttrs = { fillColor: color, fillOpacity: 0.3, strokeColor: '#000000', strokeWeight: 3 };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));

            const b = new mapboxgl.LngLatBounds();
            ring.forEach((c) => { b.extend(c); allBounds.extend(c); });
            if (!firstCenter) firstCenter = b.getCenter();

            newShapes.push({
                drawId, areaId, type: 'Polígono', name: `Polígono ${idx + 1}`, color, visible: true,
                feature: this.draw.get(drawId), center: null, radiusMeters: null, ...styleAttrs,
            });
        });

        if (newShapes.length === 0) { toast.warn('Nenhuma forma válida no arquivo.'); return; }

        // Detecta a UF pela geocodificação reversa do centróide da primeira forma.
        if (firstCenter) {
            try {
                const r = await geolocationService.reverse(firstCenter.lat, firstCenter.lng, null);
                if (r?.uf) {
                    this.setState((prev) => ({ filters: { ...prev.filters, estadosSelecionados: r.uf, estado: [r.uf] } }));
                }
            } catch { /* segue sem UF */ }
        }

        await new Promise((resolve) => this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }), resolve));
        if (!allBounds.isEmpty()) this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
        this.reloadEmpreendimentos();
    };

    // ===== Projetos =====
    getProjects = async () => {
        try {
            const res = await axios.get(route('getMapaRevendaLocacaoProjetos'));
            const options = [{ value: null, label: 'Selecione' }];
            (res.data?.projects || []).forEach((name) => options.push({ value: name, label: name }));
            this.setState({ projeto_name: options });
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao listar projetos:', e);
        }
    };

    // Serializa as formas no formato legado (intercambiável com o mapa Google):
    // polígonos como caminhos [[lat,lng],...] e círculos como {latitude, longitude, radius}.
    serializeShapesForProject = () => {
        const polygons = [];
        const circles = [];
        this.state.drawnShapes.forEach((shape) => {
            const feature = this.draw?.get(shape.drawId) || shape.feature;
            if (!feature) return;
            const style = { color: shape.fillColor, fillColor: shape.fillColor, strokeColor: shape.strokeColor, fillOpacity: shape.fillOpacity, strokeWeight: shape.strokeWeight };
            if (['circle', 'marker_circle'].includes(feature.properties?.shapeType)) {
                const center = feature.properties.center || shape.center;
                const radius = feature.properties.radiusMeters || shape.radiusMeters;
                if (!center || !radius) return;
                circles.push({ latitude: center[1], longitude: center[0], radius, name: shape.name, type: shape.type, ...style });
                return;
            }
            if (feature.geometry?.type !== 'Polygon') return;
            const ring = feature.geometry.coordinates[0] || [];
            const path = ring.slice(0, -1).map(([lng, lat]) => [lat, lng]);
            if (path.length < 3) return;
            polygons.push({ name: shape.name, polygon: path, ...style });
        });
        return { polygons, circles };
    };

    createProjectPayload = () => {
        const f = this.state.filters;
        const { polygons, circles } = this.serializeShapesForProject();
        const center = this.map?.getCenter();
        const tipoNegocio = f.tipo_negocio === 'Aluguel'
            ? [{ label: 'Locação', value: 'Aluguel' }]
            : [{ label: 'Revenda', value: 'Venda' }];

        return {
            project_name: this.state.projectName,
            tipo_negocio: tipoNegocio,
            tipo_empreendimento: f.tipo_empreendimento,
            cidade: f.cidade,
            estado: f.estado,
            bairros: f.bairros,
            quartos: f.quartos,
            vagas: f.vagas,
            polygons,
            circles,
            area_maior_que: f.area_maior_que || 0,
            area_menor_que: f.area_menor_que || 0,
            valor_maior_que: f.valor_maior_que || 0,
            valor_menor_que: f.valor_menor_que || 0,
            valor_metro_maior_que: f.valor_metro_maior_que || 0,
            valor_metro_menor_que: f.valor_metro_menor_que || 0,
            currentPosition: { lat: center?.lat ?? null, lng: center?.lng ?? null },
            raio: 3000,
        };
    };

    saveProject = async () => {
        if (!this.state.projectName.trim()) { toast.warn('Informe um nome para o projeto.'); return; }
        this.setState({ savingProject: true });
        try {
            const res = await axios.post('/mapa-revenda-locacao/projeto', this.createProjectPayload());
            toast.success(res.data?.message || 'Projeto salvo.');
            this.setState({ modalSalvarProjetoOpen: false, projectName: '' });
            this.getProjects();
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao salvar projeto:', e);
            toast.error('Erro ao salvar o projeto.');
        } finally {
            this.setState({ savingProject: false });
        }
    };

    deleteProject = async () => {
        const name = this.state.filters.projeto;
        if (!name) return;
        if (!window.confirm(`Excluir o projeto "${name}"?`)) return;
        try {
            const res = await axios.post(route('deleteMapaRevendaLocacaoProjetoByName'), [{ label: 'projeto', value: name }]);
            toast.success(res.data?.message || 'Projeto excluído.');
            this.cleanState();
            this.getProjects();
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao excluir projeto:', e);
            toast.error('Erro ao excluir o projeto.');
        }
    };

    parseJson = (value, fallback = null) => {
        if (value == null) return fallback;
        if (typeof value !== 'string') return value;
        try { return JSON.parse(value) ?? fallback; } catch { return fallback; }
    };

    handleProjetoChange = async (name) => {
        if (!name) return;
        this.cleanState();
        try {
            const res = await axios.get(route('getMapaRevendaLocacaoProjetoByName', name));
            const p = res.data?.project;
            if (!p) return;

            const tipoNegocioArr = this.parseJson(p.tipo_negocio, []);
            const tipo_negocio = tipoNegocioArr[0]?.value || 'Venda';
            const cidadeArr = this.parseJson(p.cidade, []);
            const estadoArr = this.parseJson(p.estado, []);
            const cidadeOption = cidadeArr[0] || null;

            const bairrosOptions = cidadeOption ? await this.fetchCrawlerBairros(cidadeOption.label) : [];

            const filters = {
                ...EMPTY_FILTERS,
                projeto: name,
                tipo_negocio,
                tipo_empreendimento: this.parseJson(p.tipo, []),
                estadosSelecionados: estadoArr[0] || (cidadeOption ? cidadeOption.value.split('-')[0] : null),
                estado: estadoArr.length ? estadoArr : (cidadeOption ? [cidadeOption.value.split('-')[0]] : []),
                cidade: cidadeOption ? [cidadeOption] : [],
                bairros: this.parseJson(p.bairros, []),
                bairrosOptions: bairrosOptions || [],
                area_maior_que: p.area_maior_que || null,
                area_menor_que: p.area_menor_que || null,
                valor_maior_que: p.valor_maior_que || null,
                valor_menor_que: p.valor_menor_que || null,
                valor_metro_maior_que: p.valor_metro_maior_que || null,
                valor_metro_menor_que: p.valor_metro_menor_que || null,
                quartos: this.parseJson(p.quartos, []),
                vagas: this.parseJson(p.vagas, []),
            };

            await new Promise((resolve) => this.setState({ filters }, resolve));

            await this.addProjectShapes(this.parseJson(p.polignos, []), this.parseJson(p.manualCircles, []));
            await this.reloadEmpreendimentos();
            toast.success('Projeto carregado.');
        } catch (e) {
            console.error('[MapaRevendaLocacaoMapbox] erro ao carregar projeto:', e);
            toast.error('Erro ao carregar o projeto.');
        }
    };

    addProjectShapes = async (polygons, circles) => {
        if (!this.draw) return;
        const newShapes = [];
        const allBounds = new mapboxgl.LngLatBounds();

        const register = (drawId, name, type, style) => {
            const idx = this.state.drawnShapes.length + newShapes.length;
            const color = (style.color || style.fillColor) || colors[idx % colors.length];
            const areaId = `drawn-${Date.now()}-${idx}`;
            const styleAttrs = {
                fillColor: color, fillOpacity: style.fillOpacity ?? 0.3,
                strokeColor: style.strokeColor || '#000000', strokeWeight: style.strokeWeight ?? 3,
            };
            Object.entries(styleAttrs).forEach(([k, v]) => this.draw.setFeatureProperty(drawId, k, v));
            const feature = this.draw.get(drawId);
            newShapes.push({
                drawId, areaId, type, name: name || `${type} ${idx + 1}`, color, visible: true, feature,
                center: feature?.properties?.center || null, radiusMeters: feature?.properties?.radiusMeters || null, ...styleAttrs,
            });
        };

        (polygons || []).forEach((data) => {
            const path = data?.polygon ?? data;
            if (!Array.isArray(path) || path.length < 3) return;
            const ring = path.map(([lat, lng]) => [lng, lat]);
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
            const [drawId] = this.draw.add({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } });
            register(drawId, data?.name, 'Polígono', data || {});
            ring.forEach((c) => allBounds.extend(c));
        });

        (circles || []).forEach((data) => {
            if (!data) return;
            const lat = parseFloat(data.center?.lat ?? data.latitude);
            const lng = parseFloat(data.center?.lng ?? data.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng) || !data.radius) return;
            const feature = makeCircleFeature([lng, lat], data.radius, 'circle');
            const [drawId] = this.draw.add(feature);
            register(drawId, data.name, data.type || 'Círculo', data);
            (feature.geometry.coordinates[0] || []).forEach((c) => allBounds.extend(c));
        });

        if (newShapes.length > 0) {
            await new Promise((resolve) => this.setState((prev) => ({ drawnShapes: [...prev.drawnShapes, ...newShapes] }), resolve));
            if (!allBounds.isEmpty()) this.map.fitBounds(allBounds, { padding: 60, duration: 600 });
        }
    };

    // ===== Limpar =====
    cleanState = () => {
        this.removeAddressMarker();
        this.searchContext = null;
        this.lastTileKey = null;
        this.lastClusterZoom = null;
        this.markerAbort?.abort();
        clearTimeout(this.moveTimer);
        this.currentGeojson = null;
        this.allFeatures = [];
        this.draw?.deleteAll();
        this.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
        this.map?.getSource(BAIRROS_SOURCE_ID)?.setData(EMPTY_FC);
        if (this.searchInputRef.current) this.searchInputRef.current.value = '';
        this.setState({
            filters: { ...EMPTY_FILTERS },
            drawnShapes: [],
            searchAreas: [],
            right_panel_visible: false,
            totalEmpreendimentos: 0,
            loadingClusters: false,
            showBairros: false,
            bairrosAvailable: false,
            showEmpreendimentos: true,
            showAlertCity: false,
            showImovelModal: false,
            imovelDetail: null,
        });
    };

    // ===== Busca por endereço (Google Places) =====
    setAddressMarker = (lat, lng) => {
        if (!this.map) return;
        this.addressMarker?.remove();
        this.addressMarker = new mapboxgl.Marker({ color: '#5B7537' }).setLngLat([lng, lat]).addTo(this.map);
    };

    removeAddressMarker = () => {
        this.addressMarker?.remove();
        this.addressMarker = null;
    };

    setupGooglePlaces = () => {
        loadGoogleMapsPlaces()
            .then(() => {
                if (!this.searchInputRef.current) return;
                const autocomplete = new window.google.maps.places.Autocomplete(this.searchInputRef.current, {
                    fields: ['geometry', 'formatted_address', 'name'],
                    componentRestrictions: { country: 'br' },
                });
                autocomplete.addListener('place_changed', async () => {
                    const place = autocomplete.getPlace();
                    if (!place.geometry?.location) return;
                    const lng = place.geometry.location.lng();
                    const lat = place.geometry.location.lat();
                    this.map.flyTo({ center: [lng, lat], zoom: SELECTED_ZOOM, essential: true });
                    this.setAddressMarker(lat, lng);
                    await this.loadFromAddress(lat, lng, [place]);
                });
                this.searchInputRef.current.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });
            })
            .catch((err) => console.error('[MapaRevendaLocacaoMapbox] falha ao carregar Google Places:', err));
    };

    loadFromAddress = async (lat, lng, place) => {
        let reverse;
        try { reverse = await geolocationService.reverse(lat, lng, place); }
        catch { toast.error('Não foi possível identificar a localidade.'); return; }

        const uf = reverse?.uf;
        const cidade = reverse?.cidade;
        if (!uf || !cidade) { toast.warn('Localidade sem cidade/UF identificável.'); return; }

        const cidadeOption = { value: `${uf}-${cidade}`, label: cidade };
        const bairrosOptions = await this.fetchCrawlerBairros(cidade);
        await new Promise((resolve) => this.setState((prev) => ({
            filters: { ...prev.filters, cidade: [cidadeOption], estado: [uf], estadosSelecionados: uf, bairros: [], bairrosOptions: bairrosOptions || [] },
        }), resolve));

        this.refreshBairrosAvailability([cidadeOption]);
        await this.reloadEmpreendimentos();
        if (this.searchInputRef.current) this.searchInputRef.current.value = '';
    };

    render() {
        const { left_side_bar_visible, right_panel_visible, active_tab, totalEmpreendimentos, searchAreas } = this.state;

        return (
            <>
                <div ref={this.containerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />

                <input
                    ref={this.searchInputRef}
                    type="text"
                    placeholder="Digite um endereço"
                    className="mrl-mapbox-search"
                />

                {(this.state.loading || this.state.loadingClusters) && (
                    <div className="mapbox-layer-loading" role="status">
                        <i className="fa fa-spin fa-spinner" /> Carregando imóveis…
                    </div>
                )}

                {!left_side_bar_visible && (
                    <div className="map-sidebar-show-button-wrapper">
                        <button
                            title="Mostrar Barra Lateral"
                            onClick={() => this.setState({ left_side_bar_visible: true })}
                            className="btn btn-primary btn-show-map-sidebar"
                        >
                            <i className="fas fa-angle-double-right" />
                        </button>
                    </div>
                )}

                <Sidebar visible={left_side_bar_visible} onHide={() => this.setState({ left_side_bar_visible: false })} modal={false} dismissable={false}>
                    <Tabs activeKey={active_tab} onSelect={(e) => this.setState({ active_tab: e })} id="mrl-mapbox-tabs">
                        <Tab eventKey="filtros" title="Filtros">
                            <div className="card-body" style={{ height: '80vh', overflowY: 'auto' }}>
                                <FormFiltersRevenda
                                    filters={this.state.filters}
                                    projetoOptions={this.state.projeto_name}
                                    hasAreas={this.state.drawnShapes.length > 0}
                                    showAlertCity={this.state.showAlertCity}
                                    onChange={this.handleFilterChange}
                                    onToggleQuarto={this.handleToggleQuarto}
                                    onToggleVaga={this.handleToggleVaga}
                                    onEstadoChange={this.handleEstadoChange}
                                    onCidadeChange={this.handleCidadeChange}
                                    onProjetoChange={this.handleProjetoChange}
                                    onSubmit={this.reloadEmpreendimentos}
                                    onClear={this.cleanState}
                                />
                            </div>
                        </Tab>

                        <Tab eventKey="ferramentas" title="Ferramentas">
                            <div className="card-body" style={{ height: '80vh', overflowY: 'auto' }}>
                                <div className="form-group">
                                    <label className="mb-2 d-block">Exibir no mapa</label>
                                    <div className="mapbox-tools-switches">
                                        <div className="d-flex align-items-center" title="A preferência fica salva neste navegador.">
                                            <InputSwitch
                                                inputId="check_dark_mode"
                                                checked={this.state.mapStyle === 'dark'}
                                                onChange={(e) => this.setMapStyle(e.value ? 'dark' : 'light')}
                                            />
                                            <label htmlFor="check_dark_mode" className="pl-1 mb-0 ms-2">Modo noturno</label>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <InputSwitch
                                                inputId="check_imoveis"
                                                checked={this.state.showEmpreendimentos}
                                                onChange={(e) => this.toggleEmpreendimentos(e.value)}
                                            />
                                            <label htmlFor="check_imoveis" className="pl-1 mb-0 ms-2">Exibir imóveis</label>
                                        </div>
                                        {this.state.bairrosAvailable && (
                                            <div className="d-flex align-items-center">
                                                <InputSwitch
                                                    inputId="check_bairros"
                                                    checked={this.state.showBairros}
                                                    onChange={(e) => this.handleToggleBairros(e.value)}
                                                />
                                                <label htmlFor="check_bairros" className="pl-1 mb-0 ms-2">Exibir limites de bairros</label>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <hr />

                                <div className="form-group">
                                    <label className="mb-2 d-block">Ações</label>
                                    <div className="mapbox-tools-actions">
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => this.setState({ modalSalvarProjetoOpen: true })}>
                                            <i className="fas fa-save mr-1" /> Salvar Projeto
                                        </button>
                                        {this.state.filters.projeto && (
                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={this.deleteProject}>
                                                <i className="fas fa-trash mr-1" /> Excluir Projeto
                                            </button>
                                        )}
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={this.handleDownloadKml} disabled={this.state.loadingKml}>
                                            <i className="fas fa-download mr-1" /> Exportar KML
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => this.kmlInputRef.current?.click()} disabled={this.state.loadingKml}>
                                            <i className="fas fa-upload mr-1" /> Importar KML/KMZ
                                        </button>
                                    </div>
                                    <input
                                        ref={this.kmlInputRef}
                                        type="file"
                                        accept=".kml,.kmz"
                                        style={{ display: 'none' }}
                                        onChange={(e) => { this.handleImportKmlFile(e.target.files?.[0]); e.target.value = ''; }}
                                    />
                                    {this.state.loadingKml && <small className="text-muted d-block mt-2">Processando arquivo…</small>}
                                </div>

                                {this.state.drawnShapes.length > 0 && (
                                    <>
                                        <hr />
                                        <label className="mb-2 d-block">Formas desenhadas</label>
                                        <DrawnShapesList
                                            shapes={this.state.drawnShapes}
                                            onCenter={this.centerOnShape}
                                            onToggleVisibility={this.toggleShapeVisibility}
                                            onSetRadius={this.setShapeRadius}
                                            onEditAttributes={this.editShapeAttributes}
                                            onRemove={this.removeShape}
                                        />
                                    </>
                                )}
                            </div>
                        </Tab>

                        <Tab eventKey="legendas" title="Legenda">
                            <div className="card-body" style={{ height: '80vh', overflowY: 'auto' }}>
                                <Legenda />
                            </div>
                        </Tab>
                    </Tabs>
                </Sidebar>

                {searchAreas.length > 0 && !right_panel_visible && (
                    <button className="btn mapbox-right-panel-toggle" title="Mostrar Resultados" onClick={() => this.setState({ right_panel_visible: true })}>
                        <i className="fas fa-chart-bar mr-1" />
                        <span className="mapbox-right-panel-toggle-badge">{searchAreas.length}</span>
                    </button>
                )}

                <Sidebar
                    visible={right_panel_visible}
                    position="right"
                    style={{ width: '32vw' }}
                    onHide={() => this.setState({ right_panel_visible: false })}
                    modal={false}
                    dismissable={false}
                    className="mapbox-right-panel"
                >
                    <div className="mapbox-right-panel-header">
                        <strong>Áreas selecionadas</strong>
                        <span className="badge badge-secondary ml-2">{searchAreas.length}</span>
                    </div>
                    <div className="mapbox-right-panel-body">
                        <ResultsPanel
                            areas={searchAreas}
                            onExportExcel={this.handleExportExcel}
                            onShowPolos={this.handleShowPolos}
                            onRemoveArea={this.removeArea}
                        />
                    </div>
                </Sidebar>

                <ImovelModal
                    show={this.state.showImovelModal}
                    loading={this.state.loadingImovel}
                    empreendimento={this.state.imovelDetail}
                    onHide={this.closeImovelModal}
                />

                <ShapeAttributesEditor
                    show={this.state.shapeEditorOpen}
                    shape={this.state.shapeEditing}
                    onHide={this.closeShapeEditor}
                    onApply={(attrs) => this.applyShapeAttributes(this.state.shapeEditing, attrs)}
                />

                <ModalPolosGeradores
                    show={this.state.show_modal_polos_geradores}
                    handleCloseModal={() => this.setState({ show_modal_polos_geradores: false })}
                    polos={this.state.polos_geradores_current_showing}
                    polos_geradores_agrupados={this.state.polos_geradores_current_grouped}
                    cacheKey={this.state.polos_geradores_current_cache_key}
                />

                <Dialog
                    header="Salvar Projeto"
                    visible={this.state.modalSalvarProjetoOpen}
                    style={{ width: '30vw', minWidth: 320 }}
                    onHide={() => this.setState({ modalSalvarProjetoOpen: false })}
                >
                    <div className="form-group">
                        <label>Nome do Projeto</label>
                        <InputText
                            value={this.state.projectName}
                            onChange={(e) => this.setState({ projectName: e.target.value })}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <button className="btn btn-block background-brain" onClick={this.saveProject} disabled={this.state.savingProject}>
                        {this.state.savingProject ? 'Salvando…' : 'Salvar'}
                    </button>
                </Dialog>

                <Toast ref={this.toastRef} position="top-right" />
            </>
        );
    }
}

export default MapaRevendaLocacaoMapbox;

if (document.getElementById('mapa-revenda-locacao-mapbox')) {
    ReactDOM.render(<MapaRevendaLocacaoMapbox />, document.getElementById('mapa-revenda-locacao-mapbox'));
}
